import T from 'T'
import { formatCentsToDollars, formatDollarsToCents } from 'utils/price'

import type { GridRowClassNameParams } from '@mui/x-data-grid-pro'

type LateFeeOption<V = string> = { value: V; label?: string }

export type LateFeeFormValues = {
  feeName: string
  feeRate: string
  amount: string
  amountPercent: string
  businessLine: LateFeeOption | null
  duration: number | 'CUSTOM'
  customDuration: number
  pricingPeriod: string
  startOffset: number
  startOffsetUnit: string
  generalLedger: { id: string } | null
}

export type LateFeeServerData = {
  feeName: string
  feeRate: string
  amountCents: number
  amountPercent: string
  startOffset: number
  duration: number
  pricingPeriod: string
  businessLineId: string | null
  startOffsetUnit: string
}

export const LATE_FEE_RATE = {
  FIXED: { value: 'FIXED', label: 'Dollar ($)' },
  PERCENTAGE: { value: 'PERCENTAGE', label: 'Percent (%)' },
}

export const LATE_FEE_RATE_OPTIONS = [LATE_FEE_RATE.FIXED, LATE_FEE_RATE.PERCENTAGE]

export const PERCENTAGE_DECIMAL_SCALE = 4

export const NONE_BUSINESS_LINE_OPTION = {
  label: T.NONE,
  value: '',
}

export const CUSTOM_LATE_FEE_VALUE = 'CUSTOM'

export const CUSTOM_LATE_FEE_OPTION = {
  label: T.CUSTOM,
  value: CUSTOM_LATE_FEE_VALUE,
}

export const IMMEDIATE_LATE_FEE_OPTION = {
  value: 0,
  label: T.IMMEDIATE,
}

export const INDEFINITE_LATE_FEE_OPTION = { value: 0, label: T.INDEFINITE }

export const LATE_FEE_FREQUENCY = {
  ONCE: 'ONCE',
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  ANNUALLY: 'ANNUALLY',
}

export const START_OFFSET_UNITS = {
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
  YEAR: 'YEAR',
}

export const START_OFFSET_UNIT_OPTIONS = [
  { value: START_OFFSET_UNITS.DAY, label: T.DAYS },
  { value: START_OFFSET_UNITS.WEEK, label: T.WEEKS },
  { value: START_OFFSET_UNITS.MONTH, label: T.MONTHS },
  { value: START_OFFSET_UNITS.YEAR, label: T.YEARS },
]

export const LATE_FEE_FREQUENCY_OPTIONS = [
  { value: 'ONCE', label: T.ONCE },
  { value: 'DAILY', label: T.DAILY },
  { value: 'WEEKLY', label: T.WEEKLY },
  { value: 'MONTHLY', label: T.MONTHLY },
  { value: 'QUARTERLY', label: T.QUARTERLY },
  { value: 'ANNUALLY', label: T.ANNUALLY },
]

export const getMeasureUnitLabel = (value: number, unit = ''): string => {
  const unitLowerCase = unit.toLowerCase()
  return value === 1 ? unitLowerCase.slice(0, -1) : unitLowerCase
}

const START_DATE_OPTIONS_BY_FREQUENCY: Record<string, number[]> = {
  [LATE_FEE_FREQUENCY.DAILY]: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  [LATE_FEE_FREQUENCY.WEEKLY]: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  [LATE_FEE_FREQUENCY.MONTHLY]: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  [LATE_FEE_FREQUENCY.QUARTERLY]: [1, 2, 3, 4],
  [LATE_FEE_FREQUENCY.ANNUALLY]: [1, 2, 3, 4],
}

export const getDurationMeasureUnit = (frequency: string): string => {
  switch (frequency) {
    case LATE_FEE_FREQUENCY.DAILY:
      return T.DAYS
    case LATE_FEE_FREQUENCY.WEEKLY:
      return T.WEEKS
    case LATE_FEE_FREQUENCY.MONTHLY:
      return T.MONTHS
    case LATE_FEE_FREQUENCY.QUARTERLY:
      return T.QUARTERS
    case LATE_FEE_FREQUENCY.ANNUALLY:
      return T.YEARS
    default:
      return T.DAYS
  }
}

export const getStartOffsetOptions = (frequency: string) => {
  const options = START_DATE_OPTIONS_BY_FREQUENCY[frequency] || []
  const measureUnit = getDurationMeasureUnit(frequency)

  return options.map(value => ({
    value,
    label: `${value} ${getMeasureUnitLabel(value, measureUnit)}`,
  }))
}

const DURATION_OPTIONS_BY_FREQUENCY: Record<string, number[]> = {
  [LATE_FEE_FREQUENCY.DAILY]: [30, 45, 60, 90],
  [LATE_FEE_FREQUENCY.WEEKLY]: [1, 2, 3, 4, 5, 6],
  [LATE_FEE_FREQUENCY.MONTHLY]: [1, 2, 3, 4],
  [LATE_FEE_FREQUENCY.QUARTERLY]: [1, 2, 3, 4],
  [LATE_FEE_FREQUENCY.ANNUALLY]: [1, 2, 3, 4],
}

export const getDurationOptions = (frequency: string) => {
  const options = DURATION_OPTIONS_BY_FREQUENCY[frequency] || []
  const measureUnit = getDurationMeasureUnit(frequency)

  return options.map(value => ({
    value,
    label: `${value} ${getMeasureUnitLabel(value, measureUnit)}`,
  }))
}

export const formatDataToPayload = (data: LateFeeFormValues) => {
  const {
    feeRate,
    amount,
    amountPercent,
    customDuration,
    businessLine,
    duration,
    feeName,
    pricingPeriod,
    startOffset,
    startOffsetUnit,
    generalLedger,
  } = data
  const isPricingPeriodOnce = pricingPeriod === LATE_FEE_FREQUENCY.ONCE
  const isCustomDuration = duration === 'CUSTOM'
  const businessLineId = businessLine?.value
  return {
    generalLedgerAccountId: generalLedger?.id,
    feeName,
    feeRate,
    amountCents: formatDollarsToCents(amount),
    amountPercent: parseFloat(amountPercent) || 0,
    pricingPeriod,
    businessLineId,
    startOffsetUnit,
    ...(isPricingPeriodOnce
      ? { startOffset, duration: 0 }
      : {
          startOffset,
          duration: isCustomDuration ? customDuration : duration,
        }),
  }
}

export const transformServerDataToForm = (serverData: LateFeeServerData, businessLineOptions: LateFeeOption[]) => {
  const { feeName, feeRate, amountCents, amountPercent, startOffset, duration, pricingPeriod, businessLineId, startOffsetUnit } = serverData

  const amount = formatCentsToDollars(amountCents)
  const pricingPeriodMapped =
    (pricingPeriod && LATE_FEE_FREQUENCY[pricingPeriod as keyof typeof LATE_FEE_FREQUENCY]) || LATE_FEE_FREQUENCY.ONCE
  const predefinedDurations = getDurationOptions(pricingPeriodMapped).map(option => option.value)
  const isCustomDuration = ![0, ...predefinedDurations].includes(duration)
  const isIndefiniteDuration = duration === 0
  const businessLine = businessLineOptions.find(option => option.value === businessLineId) || NONE_BUSINESS_LINE_OPTION
  return {
    feeName: feeName || '',
    feeRate: feeRate || LATE_FEE_RATE.FIXED.value,
    amount,
    amountPercent,
    businessLine,
    pricingPeriod: pricingPeriodMapped,
    startOffset,
    startOffsetUnit,
    duration: isCustomDuration ? 'CUSTOM' : duration,
    customDuration: isCustomDuration && !isIndefiniteDuration ? duration : 1,
  }
}

export const getLateFeeRowClassName = (params: GridRowClassNameParams): string | undefined => {
  const { row } = params
  if (row?.archived) return 'archived'
  return undefined
}
